import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    DataGrid,
    GridToolbarContainer,
} from "@mui/x-data-grid";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Snackbar,
    Typography,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import Draggable from "react-draggable";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import ForecastProductMultiSelectDropdown from "./ForecastProductMultiSelectDropdown";
import TextField from "@mui/material/TextField";
import convertDateStringToDate, {formatDate, formatDateForInput, getSortableDateValue, parseDate} from "../Utilities/dateUtility";
import {
    isGreaterOrEqualToZero,
    isGreaterThanZero,
    isMoneyGreaterOrEqualToZero,
    isMoneyGreaterThanZero,
    validateString
} from '../Utilities/validation';
import ResetDialog from './shared/ResetDialog';
import axiosInstance from "../AxiosInstance";
import {FileCopy} from "@mui/icons-material";
import {getChangedWeeklyDistributionRates} from "../Utilities/distributionRateUtils";
import { formatWeekHeader } from "../Utilities/weekNumberUtils";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import CustomDatePicker from "./CustomDatePicker";
import WeekRestrictedDateRangePicker from "./WeekRestrictedDateRangePicker";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import './SkuBaseForecastTable.css';
import { useColumnWidths } from '../Utilities/columnWidthUtils';

const defaultColumnWidths = {
    id: 50,
    promotionTypeId: 120,
    name: 250,
    startDate: 100,
    endDate: 100,
    buyInStart: 100,
    buyInEnd: 100,
    fixedSpend: 120,
    featureId: 120,
    stores: 96,
    totalUnits: 100,
    netRevenue: 120
};

function PaperComponent(props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}
function RRPEditForm({ data, onSave, onCancel }) {
    const theme = useTheme();
    const [value, setValue] = useState(data.currentValue || '');
    const [error, setError] = useState('');
    const isBulkEdit = data.selectedCells && data.selectedCells.length > 1;
    
    const isDark = theme.palette.mode === 'dark';

    const handleSave = () => {
        const numValue = parseFloat(value);
        if (value === '' || isNaN(numValue) || numValue < 0) {
            setError('RRP is required and must be 0 or greater');
            return;
        }
        
        setError('');
        if (isBulkEdit) {
            onSave({ value: numValue, selectedCells: data.selectedCells });
            return;
        }

        onSave({ value: numValue });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        }
    };

    return (
        <Box sx={{ pt: 1 }}>
            {isBulkEdit && (
                <Box sx={{ p: 1.5, mb: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        <strong>Bulk Edit</strong> - {data.selectedCells.length} cells selected
                    </Typography>
                </Box>
            )}
            <TextField
                label="RRP"
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                error={!!error}
                helperText={error}
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{ 
                    min: 0, 
                    step: 0.01,
                    style: { textAlign: 'right' }
                }}
                InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
                autoFocus
                onFocus={(e) => e.target.select()}
            />
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button 
                    onClick={onCancel}
                    sx={{ 
                        color: isDark ? '#fff' : '#000',
                        borderColor: isDark ? '#555' : '#ccc'
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave}
                    variant="contained"
                    sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        }
                    }}
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
}

function UnitFundingEditForm({ data, onSave, onCancel }) {
    const theme = useTheme();
    const [value, setValue] = useState(data.currentValue || '');
    const [error, setError] = useState('');
    const isBulkEdit = data.selectedCells && data.selectedCells.length > 1;
    
    const isDark = theme.palette.mode === 'dark';

    const handleSave = () => {
        const numValue = parseFloat(value);
        if (value === '' || isNaN(numValue) || numValue < 0) {
            setError('Unit Funding is required and must be 0 or greater');
            return;
        }
        
        setError('');
        if (isBulkEdit) {
            onSave({ value: numValue, selectedCells: data.selectedCells });
            return;
        }

        onSave({ value: numValue });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        }
    };

    return (
        <Box sx={{ pt: 1 }}>
            {isBulkEdit && (
                <Box sx={{ p: 1.5, mb: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        <strong>Bulk Edit</strong> - {data.selectedCells.length} cells selected
                    </Typography>
                </Box>
            )}
            <TextField
                label={data.headerName || 'Unit Funding'}
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                error={!!error}
                helperText={error}
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{ 
                    min: 0, 
                    step: 0.01,
                    style: { textAlign: 'right' }
                }}
                InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
                autoFocus
                onFocus={(e) => e.target.select()}
            />
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button 
                    onClick={onCancel}
                    sx={{ 
                        color: isDark ? '#fff' : '#000',
                        borderColor: isDark ? '#555' : '#ccc'
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave}
                    variant="contained"
                    sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        }
                    }}
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
}

function DistributionEditForm({ data, onSave, onCancel }) {
    const theme = useTheme();
    const isBulkEdit = data.selectedCells && data.selectedCells.length > 1;
    
    // Get week data for single edit
    const weekData = !isBulkEdit ? data.row?.weeklyDistributionRates?.find(w => w.week === data.field) : null;
    const originalBaseDistributionRate = weekData?.originalBaseDistributionRate || 0;
    
    // Determine initial state based on current overrides
    // Use the useBaseRate flag from data if provided, otherwise check if rate is overridden
    const initialUseBaseRate = data.useBaseRate !== undefined 
        ? data.useBaseRate 
        : !(weekData?.hasPromoRateOverride);
    
    // State for form controls
    const [useBaseRate, setUseBaseRate] = useState(initialUseBaseRate);
    const [baseValue, setBaseValue] = useState(data.baseValue || originalBaseDistributionRate);
    const [percentUplift, setPercentUplift] = useState(data.percentUplift ?? weekData?.upliftPercent ?? '');
    const [baseValueError, setBaseValueError] = useState('');
    const [percentUpliftError, setPercentUpliftError] = useState('');
    
    const isDark = theme.palette.mode === 'dark';
    
    const handleUseBaseRateToggle = (checked) => {
        setUseBaseRate(checked);
        if (!checked) {
            if (isBulkEdit) {
                setBaseValue('');
            } else {
                setBaseValue(originalBaseDistributionRate);
            }
        }
        setBaseValueError('');
    };
    
    // Calculate effective value based on current selections
    const currentBaseForCalculation = useBaseRate ? originalBaseDistributionRate : (baseValue || 0);
    const currentUplift = percentUplift === '' ? 0 : parseFloat(percentUplift) || 0;
    const effectiveValue = Math.round(currentBaseForCalculation * (1 + currentUplift / 100));
    
    // Get date range for bulk edit
    const weekDates = isBulkEdit ? data.selectedCells.map(cell => cell.field).sort() : null;
    const dateRange = isBulkEdit && weekDates ? (weekDates.length > 1 ? `${weekDates[0]} to ${weekDates[weekDates.length - 1]}` : weekDates[0]) : null;

    const handleSave = () => {
        let hasErrors = false;
        
        // Only validate base value if user is overriding it
        if (!useBaseRate && (baseValue === '' || isNaN(baseValue) || baseValue < 0)) {
            setBaseValueError('Base volume is required and must be 0 or greater');
            hasErrors = true;
        } else {
            setBaseValueError('');
        }
        
        // Validate percent uplift if provided
        if (percentUplift !== '' && (isNaN(percentUplift) || percentUplift < 0)) {
            setPercentUpliftError('Please enter a valid percentage (0 or greater)');
            hasErrors = true;
        } else {
            setPercentUpliftError('');
        }
        
        if (!hasErrors) {
            if (isBulkEdit) {
                onSave({ 
                    baseValue: useBaseRate ? null : baseValue,  // NULL = use base rate
                    percentUplift: percentUplift === '' ? null : percentUplift,  // NULL = no uplift
                    selectedCells: data.selectedCells
                });
            } else {
                onSave({ 
                    baseValue: useBaseRate ? null : baseValue,  // NULL = use base rate
                    percentUplift: percentUplift === '' ? null : percentUplift,  // NULL = no uplift
                    effectiveValue
                });
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        }
    };

    const handleResetPercentUplift = () => {
        setPercentUplift('');
        setPercentUpliftError('');
    };

    const handleResetAll = () => {
        setUseBaseRate(true);
        setBaseValue(originalBaseDistributionRate);
        setPercentUplift('');
        setBaseValueError('');
        setPercentUpliftError('');
    };

    return (
        <Box sx={{ pt: 1 }}>
            {/* Header with context info */}
            <Box sx={{ 
                mb: 3,
                p: 2,
                backgroundColor: isDark ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.primary.main}`
            }}>
                {isBulkEdit ? (
                    <>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
                            <strong>Bulk Edit</strong> - {data.selectedCells.length} cells selected
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Date range: {dateRange}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.secondary }}>
                            Week {data.weekDate}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            <strong>{data.productName}</strong>
                        </Typography>
                    </>
                )}
            </Box>

            {/* Current Values Display - Only for single edit */}
            {!isBulkEdit && (
                <Box sx={{ 
                    mb: 3,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 2
                }}>
                    <Box sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        backgroundColor: isDark ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)',
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.primary.main}`
                    }}>
                        <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                            BASE RATE
                        </Typography>
                        <Typography variant="h5" sx={{ 
                            color: theme.palette.primary.main, 
                            fontWeight: 700, 
                            fontSize: '1.5rem',
                        wordBreak: 'break-all',
                        lineHeight: 1.2
                    }}>
                        {originalBaseDistributionRate.toLocaleString()}
                    </Typography>
                </Box>
                
                <Box sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    backgroundColor: currentUplift > 0 
                        ? (isDark ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)')
                        : (isDark ? 'rgba(158, 158, 158, 0.1)' : 'rgba(158, 158, 158, 0.05)'),
                    borderRadius: '8px',
                    border: `1px solid ${currentUplift > 0 ? theme.palette.success.main : theme.palette.grey[500]}`
                }}>
                    <Typography variant="caption" sx={{ 
                        color: currentUplift > 0 ? theme.palette.success.main : theme.palette.grey[600], 
                        fontWeight: 600 
                    }}>
                        UPLIFT
                    </Typography>
                    <Typography variant="h5" sx={{ 
                        color: currentUplift > 0 ? theme.palette.success.main : theme.palette.text.secondary, 
                        fontWeight: 700,
                        fontSize: '1.5rem'
                    }}>
                        {currentUplift > 0 ? '+' : ''}{currentUplift || 0}%
                    </Typography>
                </Box>
                
                <Box sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    backgroundColor: isDark ? 'rgba(158, 158, 158, 0.1)' : 'rgba(158, 158, 158, 0.05)',
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.grey[500]}`
                }}>
                    <Typography variant="caption" sx={{ color: theme.palette.grey[600], fontWeight: 600 }}>
                        TOTAL VOLUME
                    </Typography>
                    <Typography variant="h5" sx={{ 
                        color: theme.palette.text.primary, 
                        fontWeight: 700, 
                        fontSize: '1.5rem',
                        wordBreak: 'break-all',
                        lineHeight: 1.2
                    }}>
                        {effectiveValue.toLocaleString()}
                    </Typography>
                </Box>
                </Box>
            )}

            {/* Override Controls */}
            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={useBaseRate}
                            onChange={(e) => handleUseBaseRateToggle(e.target.checked)}
                            color="primary"
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                                Use Base Volume
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                Volume will update automatically if Distribution or Rate of Sale changes
                            </Typography>
                        </Box>
                    }
                />
            </Box>

            {/* Edit Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {/* Base Distribution Field - only visible when NOT using base rate */}
                {!useBaseRate && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                                Override Base Volume
                            </Typography>
                        </Box>
                        <TextField
                            type="number"
                            value={baseValue}
                            onChange={(e) => setBaseValue(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                            onKeyDown={handleKeyDown}
                            error={Boolean(baseValueError)}
                            helperText={baseValueError}
                            placeholder="Enter base volume"
                            fullWidth
                            inputProps={{ min: 0 }}
                            size="small"
                        />
                    </Box>
                )}

                {/* Percent Uplift Field */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justify: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                            Percent Uplift (%)
                        </Typography>
                        {percentUplift !== '' && percentUplift > 0 && (
                            <Button 
                                variant="text" 
                                size="small" 
                                onClick={handleResetPercentUplift}
                                sx={{ minWidth: 'auto', px: 1 }}
                            >
                                Clear
                            </Button>
                        )}
                    </Box>
                    <TextField
                        type="number"
                        value={percentUplift}
                        autoFocus
                        onChange={(e) => {
                            let value = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                            if (value > 1000) value = 1000;
                            if (value !== 0 && value !== '') {
                                value = Math.round(value * 100) / 100;
                            }
                            setPercentUplift(value);
                        }}
                        onKeyDown={handleKeyDown}
                        error={Boolean(percentUpliftError)}
                        helperText={percentUpliftError}
                        placeholder="Enter percent uplift (optional)"
                        fullWidth
                        inputProps={{ 
                            step: 1,
                            min: 0
                        }}
                        size="small"
                    />
                </Box>
            </Box>

            {/* Reset All Button */}
            <Box sx={{ mb: 2 }}>
                <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleResetAll}
                    color="warning"
                >
                    Reset All
                </Button>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2 }}>
                <Button onClick={onCancel} color="inherit">
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained"
                >
                    Apply
                </Button>
            </Box>
        </Box>
    );
}

function PromoEditToolbar({ title, onAdd, onEdit, onDelete, onClone, selectedPromotion }) {
    return (
        <GridToolbarContainer sx={{ justifyContent: "space-between", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h6" sx={{ mr: 2, pl: 1 }}>
                    {title}
                </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Create">
                    <span>
                        <IconButton aria-label="createPromotion" onClick={onAdd}>
                            <AddIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Clone">
                    <span>
                        <IconButton aria-label="clone" onClick={onClone} disabled={!selectedPromotion}>
                            <FileCopy />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Edit">
                    <span>
                        <IconButton aria-label="edit" onClick={onEdit} disabled={!selectedPromotion}>
                            <EditIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Delete">
                    <span>
                        <IconButton
                            aria-label="delete"
                            onClick={() => onDelete(selectedPromotion)}
                            disabled={!selectedPromotion}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </GridToolbarContainer>
    );
}

/**
 * @param {{
 *   forecastId: any,
 *   forecastPeriodId: any,
 *   customerId: any,
 *   onRefreshSkuBaseForecasts: any,
 *   onPromotionProductsDataChange: any,
 *   promotions: any,
 *   onRefreshPromotions: any,
 *   isForecastPeriodLocked: any,
 *   allowPastWeeks?: boolean,
 *   skuBaseForecasts: any,
 *   highlightedPromotionId?: number | null,
 *   highlightedPromotionTrigger?: number
 * }} props
 */
export default function PromotionsTable({
                                            forecastId,
                                            forecastPeriodId,
                                            customerId,
                                            onRefreshSkuBaseForecasts,
                                            onPromotionProductsDataChange,
                                            promotions,
                                            onRefreshPromotions,
                                            isForecastPeriodLocked,
                                            allowPastWeeks = false,
                                            skuBaseForecasts,
                                            highlightedPromotionId = null,
                                            highlightedPromotionTrigger = 0
                                        }) {
    const apiRef = useGridApiRef();
    const theme = useTheme();
    const { columnWidths, handleColumnResize } = useColumnWidths('promotions', defaultColumnWidths);
    const [selectedCells, setSelectedCells] = useState([]);
    const [isGridReady, setIsGridReady] = useState(false);
    const [rows, setRows] = useState([]);
    const [selectedPromotionId, setSelectedPromotionId] = useState(null);
    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [snackbar, setSnackbar] = useState(null);
    const [features, setFeatures] = useState([]);
    const [promotionTypes, setPromotionTypes] = useState([]);
    const [buyInDateOptions, setBuyInDateOptions] = useState([]);
    const [openPromotionProductDialog, setOpenPromotionProductDialog] = useState(false);
    const [promotionProductFormData, setPromotionProductFormData] = useState({
        recommendedRetailPrice: 0,
        unitFunding: 0,
        skuBaseForecastId: []
    });
    const [openPromotionDialog, setOpenPromotionDialog] = useState(false);
    const [promotionFormData, setPromotionFormData] = useState({
        promotionTypeId: 1,
        name: "",
        startDate: "",
        endDate: "",
        buyInStart: "",
        buyInEnd: "",
        fixedSpend: 0,
        featureId: 1,
        stores: 0,
    });
    const [skuBaseForecastErrorText, setSkuBaseForecastErrorText] = useState('');
    const [recommendedRetailPriceErrorText, setRecommendedRetailPriceErrorText] = useState('');
    const [unitFundingErrorText, setUnitFundingErrorText] = useState('');
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [selectedPromotionProductId, setSelectedPromotionProductId] = useState(null);
    const [selectedPromotionProduct, setSelectedPromotionProduct] = useState(null);
    const [confirmingDeletionType, setConfirmingDeletionType] = useState(null);
    const [editedPromotionProductRows, setEditedPromotionProductRows] = useState({});
    const [promotionProductsData, setPromotionProductsData] = useState([]);
    const [percentUpliftValues, setPercentUpliftValues] = useState({});
    const [openDistributionEditDialog, setOpenDistributionEditDialog] = useState(false);
    const [distributionEditData, setDistributionEditData] = useState(null);
    const [openBulkDistributionEditDialog, setOpenBulkDistributionEditDialog] = useState(false);
    const [bulkDistributionEditData, setBulkDistributionEditData] = useState(null);
    
    // RRP and Unit Funding edit dialog states
    const [openRRPEditDialog, setOpenRRPEditDialog] = useState(false);
    const [rrpEditData, setRRPEditData] = useState(null);
    const [openUnitFundingEditDialog, setOpenUnitFundingEditDialog] = useState(false);
    const [unitFundingEditData, setUnitFundingEditData] = useState(null);
    
    // Reset override dialog state
    const [resetDialog, setResetDialog] = useState({
        open: false,
        promotionProductId: null,
        week: '',
        currentValue: 0,
        baseValue: 0
    });
    const [unsavedCellChanges, setUnsavedCellChanges] = useState(new Set());
    const [loadingPromotions, setLoadingPromotions] = useState(false);
    const [loadingPromotionProducts, setLoadingPromotionProducts] = useState(false);
    const isCreatingPromotionProductRef = useRef(false);
    const isUpsertingPromotionRef = useRef(false);
    const lastHandledHighlightTriggerRef = useRef(0);

    const [promotionTypeIdError, setPromotionTypeIdError] = useState('');
    const [nameError, setNameError] = useState('');
    const [fixedSpendError, setFixedSpendError] = useState('');
    const [startDateError, setStartDateError] = useState('');
    const [endDateError, setEndDateError] = useState('');
    const [featureIdError, setFeatureIdError] = useState('');
    const [storesError, setStoresError] = useState('');
    const [buyInStartError, setBuyInStartError] = useState('');
    const [buyInEndError, setBuyInEndError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);

    const getWeekPosition = useCallback((weekString) => {
        if (!skuBaseForecasts || skuBaseForecasts.length === 0)
            return 0;
        
        const allWeeks = skuBaseForecasts[0]?.weeklyDistributionRates?.map(wdr => wdr.week) || [];
        
        const position = allWeeks.indexOf(weekString);
        return position >= 0 ? position : 0;
    }, [skuBaseForecasts]);
    const [isCloneMode, setIsCloneMode] = useState(false);


    const handleOpenConfirmationDialog = (type) => {
        setConfirmingDeletionType(type);
        setOpenConfirmationDialog(true);
    };

    const handleCloseConfirmationDialog = () => {
        setConfirmingDeletionType(null);
        setOpenConfirmationDialog(false);
    };

    const handleConfirmDeletePromotion = async () => {
        try {
            setLoadingPromotions(true);
            const response = await axiosInstance.delete(`/forecasts/${forecastId}/promotions/${selectedPromotion.id}?forecastPeriodId=${forecastPeriodId}`);

            if (response.status === 204 || response.status === 200) {
                setRows(rows.filter(row => row.id !== selectedPromotion.id));

                // Remove the deleted promotion's SKUs from the highlighted list
                onPromotionProductsDataChange(null);

                if (response.status === 204) {
                    setSnackbar({children: "Promotion successfully deleted", severity: "success"});
                } else if (response.status === 200) {
                    setSnackbar({children: "Promotion successfully deactivated", severity: "success"});
                }
            }
        } catch (error) {
            console.error(`Error deleting the promotion: ${error}`);
            setSnackbar({
                children: `Error deleting the promotion: ${error.response?.data || error.message}`,
                severity: "error"
            });
        } finally {
            try {
                onRefreshSkuBaseForecasts();
            } catch (refreshError) {
                console.error('Error refreshing SKU base forecasts:', refreshError);
            }
            setSelectedPromotion(null);
            setSelectedPromotionId(null);
            setLoadingPromotions(false);
        }
        handleCloseConfirmationDialog();
    };

    const handleConfirmDeletePromotionProduct = async () => {
        if (!selectedPromotionProduct) {
            setSnackbar({children: "No promotion product selected", severity: "error"});
            return;
        }

        try {
            setLoadingPromotionProducts(true);
            const response = await axiosInstance.delete(`/forecasts/${forecastId}/promotions/${selectedPromotionId}/promotionProducts/${selectedPromotionProductId}?forecastPeriodId=${forecastPeriodId}`);

            if (response.status === 204 || response.status === 200) {
                const updatedPromotionProducts = selectedPromotion.promotionProducts.filter(pp => pp.promotionProductId !== selectedPromotionProductId);
                setSelectedPromotion({...selectedPromotion, promotionProducts: updatedPromotionProducts});

                const updatedPromotionProductsData = promotionProductsData.filter(
                    pp => pp.skuBaseForecastId !== selectedPromotionProduct.skuBaseForecastId
                );
                setPromotionProductsData(updatedPromotionProductsData);
                onPromotionProductsDataChange(updatedPromotionProductsData);
                
                try {
                    await onRefreshPromotions();
                } catch (refreshError) {
                    console.error('Error refreshing promotions:', refreshError);
                }

                if (response.status === 204) {
                    setSnackbar({children: "Promotion product successfully deleted", severity: "success"});
                } else if (response.status === 200) {
                    setSnackbar({children: "Promotion product successfully deactivated", severity: "success"});
                }
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 409) {
                    setSnackbar({
                        children: `Conflict: ${error.response.data.message}`,
                        severity: "warning"
                    });
                } else {
                    setSnackbar({
                        children: `Error deleting the promotion product: ${error.response.data || error.message}`,
                        severity: "error"
                    });
                }
            } else {
                setSnackbar({
                    children: `Error deleting the promotion product: ${error.message}`,
                    severity: "error"
                });
            }
        } finally {
            // Always refresh SKU base forecasts to ensure consistency, even if deletion failed
            try {
                onRefreshSkuBaseForecasts();
            } catch (refreshError) {
                console.error('Error refreshing SKU base forecasts:', refreshError);
            }
            setSelectedPromotionProduct(null);
            setSelectedPromotionProductId(null);
            handleCloseConfirmationDialog();
            setLoadingPromotionProducts(false);
        }
    };


    useEffect(() => {
        setRows(promotions);
    }, [promotions]);

    useEffect(() => {
        getBuyInDateOptions();
    }, [forecastId]);

    useEffect(() => {
        // Set grid as ready after component mounts
        setIsGridReady(true);
    }, []);

    useEffect(() => {
        if (selectedPromotionId) {
            const updatedSelectedPromotion = rows.find(p => p.id === selectedPromotionId);
            setSelectedPromotion(updatedSelectedPromotion);
        }
    }, [rows, selectedPromotionId]);

    const clearPromotionProductEdits = useCallback(() => {
        setUnsavedCellChanges(new Set());
        setEditedPromotionProductRows({});
    }, []);

    useEffect(() => {
        if (highlightedPromotionTrigger <= 0 || lastHandledHighlightTriggerRef.current === highlightedPromotionTrigger) {
            return;
        }

        if (!highlightedPromotionId) {
            lastHandledHighlightTriggerRef.current = highlightedPromotionTrigger;
            clearPromotionProductEdits();
            setSelectedPromotionId(null);
            setSelectedPromotion(null);
            return;
        }

        const promotion = rows.find(p => p.id === highlightedPromotionId);
        if (promotion) {
            lastHandledHighlightTriggerRef.current = highlightedPromotionTrigger;
            clearPromotionProductEdits();
            setSelectedPromotionId(highlightedPromotionId);
            setSelectedPromotion(promotion);
        }
    }, [clearPromotionProductEdits, highlightedPromotionId, highlightedPromotionTrigger, rows]);

    useEffect(() => {
        const fetchFeatures = async () => {
            try {
                setLoadingPromotions(true);
                const response = await axiosInstance.get('/features');
                setFeatures(response.data);
            } catch (error) {
                console.error('Error fetching features:', error);
            } finally {
                setLoadingPromotions(false);
            }
        };

        fetchFeatures();
    }, []);

    useEffect(() => {
        const fetchPromoTypes = async () => {
            try {
                setLoadingPromotions(true);
                const response = await axiosInstance.get('/promotionTypes');
                setPromotionTypes(response.data);
            } catch (error) {
                console.error('Error fetching promotion types:', error);
            } finally {
                setLoadingPromotions(false);
            }
        };

        fetchPromoTypes();
    }, []);

    const getBuyInDateOptions = async () => {
        try {
            setLoadingPromotions(true);
            const response = await axiosInstance.get(`/forecasts/${forecastId}/promotions/buyInDateOptions`);
            const buyInDateOptions = response.data;
            setBuyInDateOptions(buyInDateOptions);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingPromotions(false);
        }
    }

    useCallback((error) => {
        setSnackbar({children: error.message, severity: 'error'});
    }, []);
    const handleCloseSnackbar = () => setSnackbar(null);
    
    // Reset override dialog handlers
    const handleResetDialogClose = () => {
        setResetDialog({ ...resetDialog, open: false });
    };
    
    const handleResetOverrideConfirm = () => {
        const { promotionProductId, week } = resetDialog;
        const cellKey = `${promotionProductId}-${week}`;
        
        const row = selectedPromotion?.promotionProducts?.find(r => r.promotionProductId === promotionProductId);
        
        if (row) {
            // Get the current edited row if it exists
            const editedRow = editedPromotionProductRows[promotionProductId];
            const currentWeeklyRates = editedRow?.weeklyDistributionRates || row.weeklyDistributionRates;
            
            // Set both basePromoDistributionRate and uplift to NULL to delete the override
            const updatedWeeklyRates = currentWeeklyRates.map(w => 
                w.week === week 
                    ? { ...w, basePromoDistributionRate: null }  // NULL = use base rate
                    : w
            );
            
            const updatedRow = {
                ...(editedRow || row),
                weeklyDistributionRates: updatedWeeklyRates
            };
            
            setEditedPromotionProductRows(prev => ({
                ...prev,
                [promotionProductId]: updatedRow
            }));
            
            apiRef.current.updateRows([updatedRow]);
            
            // Set uplift to NULL (will be sent as NULL to server for deletion)
            setPercentUpliftValues(prev => ({
                ...prev,
                [cellKey]: null  // NULL = no uplift
            }));
            
            setUnsavedCellChanges(prev => new Set(prev).add(cellKey));
        }
        
        setResetDialog({ ...resetDialog, open: false });
    };
    
    const handlePromotionRowClick = (params) => {
        if (params.row.isNew) {
            setSelectedPromotion(null);
            setSelectedPromotionId(null);
            return;
        }

        const promotionProducts = params.row.promotionProducts.map(pp => ({
            skuBaseForecastId: pp.skuBaseForecastId,
            weeklyDistributionRates: pp.weeklyDistributionRates.map(rate => rate.week)
        }));

        setPromotionProductsData(promotionProducts);
        onPromotionProductsDataChange(promotionProducts);

        // Clear unsaved changes when switching promotions
        clearPromotionProductEdits();

        setSelectedPromotionId(params.id);
    };

    useEffect(() => {
        if (selectedPromotionId) {
            const updatedSelectedPromotion = rows.find(p => p.id === selectedPromotionId);
            setSelectedPromotion(updatedSelectedPromotion);

            if (updatedSelectedPromotion?.promotionProducts) {
                const promotionProducts = updatedSelectedPromotion.promotionProducts.map(pp => ({
                    skuBaseForecastId: pp.skuBaseForecastId,
                    weeklyDistributionRates: pp.weeklyDistributionRates.map(rate => rate.week)
                }));

                setPromotionProductsData(promotionProducts);
                onPromotionProductsDataChange(promotionProducts);

                // Initialize uplift values from backend data
                const backendUpliftValues = {};
                updatedSelectedPromotion.promotionProducts.forEach((pp) => {
                    pp.weeklyDistributionRates.forEach((wdr) => {
                        const cellKey = `${pp.promotionProductId}-${wdr.week}`;
                        // Use uplift percent from backend if it exists
                        if (wdr.upliftPercent !== null && wdr.upliftPercent !== undefined) {
                            backendUpliftValues[cellKey] = wdr.upliftPercent;
                        }
                    });
                });
                setPercentUpliftValues(backendUpliftValues);
            }
        }
    }, [selectedPromotionId, rows]);

    const handleCreatePromotionDialog = () => {
        setSelectedPromotion(null);
        setIsEditMode(false);
        resetPromotionForm();
        setOpenPromotionDialog(true);
    };

    const resetPromotionForm = () => {
        setPromotionFormData({
            promotionTypeId: 1,
            name: "",
            startDate: "",
            endDate: "",
            buyInStart: "",
            buyInEnd: "",
            fixedSpend: 0,
            featureId: 1,
            stores: 0,
        });
        setPromotionTypeIdError('');
        setNameError('');
        setFixedSpendError('');
        setStartDateError('');
        setEndDateError('');
        setFeatureIdError('');
        setStoresError('');
        setBuyInStartError('');
        setBuyInEndError('');
    }

    const handleCloseDialogs = () => {
        setOpenPromotionDialog(false);
        setOpenPromotionProductDialog(false);
    };

    const handleUpsertPromotion = async (data) => {
        if (isUpsertingPromotionRef.current) {
            return;
        }

        const isEditMode = !!selectedPromotion && !isCloneMode;
        
        const isValidPromotionTypeId = isGreaterThanZero(data.promotionTypeId);
        const isValidName = validateString(data.name);
        const isValidFixedSpend = isMoneyGreaterOrEqualToZero(data.fixedSpend);

        const requiresExtendedValidation = data.promotionTypeId !== 2;

        let isValidStartDate = true;
        let isValidEndDate = true;
        let isValidDatesOrder = true;

        const startDate = new Date(data.startDate);
        const endDate = new Date(data.endDate);
        if (requiresExtendedValidation) {
            isValidStartDate = !isNaN(startDate.getTime());
            isValidEndDate = !isNaN(endDate.getTime());
            isValidDatesOrder = endDate >= startDate;
        } else {
            data.startDate = null;
            data.endDate = null;
            data.featureId = null;
            data.stores = null;
        }
        
        const buyInStart = parseDate(data.buyInStart);
        const buyInEnd = parseDate(data.buyInEnd);

        const isValidBuyInStart = Boolean(buyInStart);
        const isValidBuyInEnd = Boolean(buyInEnd);
        
        const isBuyInDatesOrderCorrect = buyInEnd >= buyInStart;
        
        const isValidFeatureId = !requiresExtendedValidation || isGreaterThanZero(data.featureId);
        const isValidStores = !requiresExtendedValidation || isGreaterOrEqualToZero(data.stores);

        setPromotionTypeIdError(isValidPromotionTypeId ? "" : "Please select a valid promotion type");
        setNameError(isValidName ? "" : "Please enter a valid value for Promotion Name");
        setFixedSpendError(isValidFixedSpend ? "" : "Please enter a valid value for Fixed Spend");

        if (requiresExtendedValidation) {
            setStartDateError(isValidStartDate ? "" : "Please enter a valid start date");
            if (!isValidEndDate) {
                setEndDateError("Please enter a valid end date");
            } else if (!isValidDatesOrder) {
                setEndDateError("End date must be on or after start date");
            } else {
                setEndDateError("");
            }
            setFeatureIdError(isValidFeatureId ? "" : "Please select a valid feature");
            setStoresError(isValidStores ? "" : "Please enter a valid number of stores");
        }
        setBuyInStartError(isValidBuyInStart ? "" : "Please select a valid Buy In Start date");
        
        if (!isValidBuyInEnd) {
            setBuyInEndError("Please select a valid Buy In End date");
        } else if (!isBuyInDatesOrderCorrect) {
            setBuyInEndError("Buy In End date must be on or after Buy In Start date");
        } else {
            setBuyInEndError("");
        }

        if (isCloneMode && requiresExtendedValidation) {
            const clonedStart = parseDate(selectedPromotion.buyInStart);
            const clonedEnd = parseDate(selectedPromotion.buyInEnd);
            const extendedClonedEnd = new Date(clonedEnd);
            extendedClonedEnd.setDate(clonedEnd.getDate() + 6);

            const isBuyInOverlapping =
                (buyInStart >= clonedStart && buyInStart <= extendedClonedEnd) ||
                (buyInEnd >= clonedStart && buyInEnd <= extendedClonedEnd);

            if (isBuyInOverlapping) {
                setBuyInStartError("Buy In Start overlaps with the cloned promotion");
                setBuyInEndError("Buy In End overlaps with the cloned promotion");
            }

            const clonedStartDate = new Date(selectedPromotion.startDate);
            const clonedEndDate = new Date(selectedPromotion.endDate);

            const isStartEndOverlapping =
                startDate &&
                endDate &&
                clonedStartDate &&
                clonedEndDate &&
                (
                    (startDate >= clonedStartDate && startDate <= clonedEndDate) || // Start date overlaps
                    (endDate >= clonedStartDate && endDate <= clonedEndDate) || // End date overlaps
                    (startDate <= clonedStartDate && endDate >= clonedEndDate) // Completely contains the cloned range
                );

            if (isStartEndOverlapping) {
                setStartDateError("Start date overlaps with the cloned promotion");
                setEndDateError("End date overlaps with the cloned promotion");
            }

            if (isBuyInOverlapping || isStartEndOverlapping) {
                return;
            }
        }

        if (
            !isValidPromotionTypeId ||
            !isValidName ||
            !isValidFixedSpend ||
            (requiresExtendedValidation &&
                (!isValidStartDate || !isValidEndDate || !isValidDatesOrder || !isValidFeatureId || !isValidStores)) ||
            !isValidBuyInStart ||
            !isValidBuyInEnd ||
            !isBuyInDatesOrderCorrect
        ) {
            return;
        }

        setPromotionTypeIdError("");
        setNameError("");
        setFixedSpendError("");
        if (requiresExtendedValidation) {
            setStartDateError("");
            setEndDateError("");
            setFeatureIdError("");
            setStoresError("");
        }
        setBuyInStartError("");
        setBuyInEndError("");

        data.customerId = customerId;
        data.forecastPeriodId = forecastPeriodId;

        try {
            isUpsertingPromotionRef.current = true;
            setLoadingPromotions(true);
            if (isCloneMode) {
                const response = await axiosInstance.post(`/forecasts/${forecastId}/promotions/clone/${selectedPromotion.id}`, data);
                const clonedPromotion = response.data;
                setRows((prevRows) => [...prevRows, clonedPromotion]);
                setSelectedPromotionId(clonedPromotion.id);
                setSelectedPromotion(clonedPromotion);
                setSnackbar({ children: "Promotion successfully cloned", severity: "success" });
            } else if (isEditMode) {
                const updatedData = { ...data, id: selectedPromotion.id };
                await axiosInstance.put(`/forecasts/${forecastId}/promotions`, updatedData);
                setSnackbar({ children: 'Promotion successfully updated', severity: 'success' });
            } else {
                const response = await axiosInstance.post(`/forecasts/${forecastId}/promotions`, data);
                const createdPromotion = response.data;
                setRows(prevRows => [...prevRows, createdPromotion]);
                setSelectedPromotionId(createdPromotion.id);
                setSelectedPromotion(createdPromotion);
                setSnackbar({ children: 'Promotion successfully created', severity: 'success' });
            }

            try {
                await onRefreshPromotions();
            } catch (refreshError) {
                console.error('Error refreshing promotions:', refreshError);
            }
            handleCloseDialogs();
            setIsCloneMode(false);
        } catch (error) {
            console.error("API Error Response:", error.response);

            let errorMessage = "An unexpected error occurred.";

            if (error.response?.data) {
                const { data } = error.response;

                if (typeof data === "string") {
                    errorMessage = data;
                }
                else if (data.errors) {
                    errorMessage = Object.values(data.errors).flat().join(" ");
                }
                else if (data.message) {
                    errorMessage = data.message;
                }
                else if (data.title) {
                    errorMessage = data.title;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            setSnackbar({ children: String(errorMessage), severity: "error" });
        } finally {
            try {
                onRefreshSkuBaseForecasts();
            } catch (refreshError) {
                console.error('Error refreshing SKU base forecasts:', refreshError);
            }
            setLoadingPromotions(false);
            isUpsertingPromotionRef.current = false;
        }
    };

    const handleRowSelection = (newSelectionModel) => {
        const newSelectedId = newSelectionModel[0] || null;
        setSelectedPromotionId(newSelectedId);
    };

    const handleDeleteClick = () => {
        if (!selectedPromotion || !selectedPromotion.id) {
            console.error("No promotion selected for deletion.");
            setSnackbar({children: "No promotion selected", severity: "error"});
            return;
        }
        handleOpenConfirmationDialog('promotion');
    };
    
    const handleEditPromotionDialog = async () => {
        if (!selectedPromotion) return;

        resetPromotionForm();
        setIsEditMode(true);
        setIsCloneMode(false);
        setPromotionFormData({
            promotionTypeId: selectedPromotion.promotionTypeId,
            name: selectedPromotion.name,
            startDate: formatDateForInput(selectedPromotion.startDate),
            endDate: formatDateForInput(selectedPromotion.endDate),
            buyInStart: selectedPromotion.buyInStart,
            buyInEnd: selectedPromotion.buyInEnd,
            fixedSpend: selectedPromotion.fixedSpend,
            featureId: selectedPromotion.featureId,
            stores: selectedPromotion.stores,
        });

        setOpenPromotionDialog(true);
    }
    
    const handleClonePromotionDialog = async () => {
        if (!selectedPromotion) return;

        setIsEditMode(false);
        setIsCloneMode(true);
        setPromotionFormData({
            promotionTypeId: selectedPromotion.promotionTypeId,
            name: `${selectedPromotion.name} - Clone`,
            startDate: formatDateForInput(selectedPromotion.startDate),
            endDate: formatDateForInput(selectedPromotion.endDate),
            buyInStart: selectedPromotion.buyInStart,
            buyInEnd: selectedPromotion.buyInEnd,
            fixedSpend: selectedPromotion.fixedSpend,
            featureId: selectedPromotion.featureId,
            stores: selectedPromotion.stores,
        });

        setOpenPromotionDialog(true);
    }
    
    const columns = [
        { field: "id", headerName: "ID", width: columnWidths.id },
        {
            field: "promotionTypeId",
            headerName: "Type",
            width: columnWidths.promotionTypeId,
            valueGetter: (value, row) => {
                const promotionType = promotionTypes.find(
                    (type) => type.id === value
                );
                return promotionType ? promotionType.name : "";
            },
        },
        { field: "name", headerName: "Name", width: columnWidths.name },
        {
            field: 'startDate',
            headerName: 'Promo Start',
            type: 'date',
            width: columnWidths.startDate,
            valueGetter: (value) => getSortableDateValue(value),
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: 'endDate',
            headerName: 'Promo End',
            type: 'date',
            width: columnWidths.endDate,
            valueGetter: (value) => getSortableDateValue(value),
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: "buyInStart",
            headerName: "Buy In Start",
            type: 'date',
            width: columnWidths.buyInStart,
            valueGetter: (value) => getSortableDateValue(value),
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: "buyInEnd",
            headerName: "Buy In End",
            type: 'date',
            width: columnWidths.buyInEnd,
            valueGetter: (value) => getSortableDateValue(value),
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: "fixedSpend",
            headerName: "Fixed Spend",
            type: 'number',
            width: columnWidths.fixedSpend,
            valueFormatter: (value) => {
                return '£' + parseFloat(value).toFixed(2);
            },
        },
        {
            field: "featureId",
            headerName: "Feature",
            width: columnWidths.featureId,
            valueFormatter: (value) => {
                if (value === null) return '';  // Return empty if value is null
                const feature = features.find(f => f.featureId === value);
                return feature ? feature.name : '';
            },
        },
        {
            field: "stores",
            headerName: "Stores",
            type: "number",
            width: columnWidths.stores,
            valueFormatter: (value) => value !== null ? value : '',  // Display nothing if stores is null
        },
        {
            field: 'totalUnits',
            headerName: 'Total Units',
            type: 'number',
            width: columnWidths.totalUnits,
        },
        {
            field: 'netRevenue',
            headerName: 'Net Rev',
            type: 'number',
            width: columnWidths.netRevenue,
            valueFormatter: (value) => value !== null && value !== undefined ? `£${value.toFixed(2)}` : '',
        },
    ];

    const getEditabilityForField = useCallback((field) => {
        if (!selectedPromotion) return false;
        
        if (/^\d{2}-\d{2}-\d{4}$/.test(field)) {
            const [day, month, year] = field.split('-').map(Number);
            const columnDate = new Date(year, month - 1, day);
            columnDate.setHours(0, 0, 0, 0);
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            return (allowPastWeeks || columnDate > today) && !isForecastPeriodLocked;
        }
        
        if (field === 'recommendedRetailPrice' || field === 'unitFunding') {
            const buyInEndDate = convertDateStringToDate(selectedPromotion?.buyInEnd);
            if (buyInEndDate) {
                buyInEndDate.setHours(0, 0, 0, 0);
            }
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isReadOnly = !allowPastWeeks && buyInEndDate ? today > buyInEndDate : false;
            
            return !isReadOnly && !isForecastPeriodLocked;
        }
        
        return false;
    }, [selectedPromotion, isForecastPeriodLocked, allowPastWeeks]);

    const promotionProductColumns = useMemo(() => {
        if (!selectedPromotion || !selectedPromotion.promotionProducts) {
            return [];
        }

        const staticColumns = [
            {field: 'promotionProductId', headerName: 'ID', width: 100, headerAlign: 'center'},
            {field: 'skuBaseForecastId', headerName: 'SBF ID', width: 100, headerAlign: 'center'},
            {
                field: 'skuDescription', 
                headerName: 'SKU Description', 
                width: 200,
                align: 'left',
                headerAlign: 'center',
                cellClassName: 'leftAlignedCell'
            },
            {
                field: 'recommendedRetailPrice',
                headerName: 'RRP',
                type: 'number',
                width: 100,
                editable: false, // Disable default editing since we handle it in custom cell
                sortable: false,
                headerAlign: 'center',
                renderCell: (params) => (
                    <RRPCell 
                        params={params} 
                        isEditable={getEditabilityForField('recommendedRetailPrice')}
                        theme={theme}
                        isForecastPeriodLocked={isForecastPeriodLocked}
                    />
                ),
                valueGetter: (value, row) => {
                    // Get current value including any unsaved edits
                    const editedRow = editedPromotionProductRows[row.promotionProductId];
                    return editedRow ? editedRow.recommendedRetailPrice : row.recommendedRetailPrice;
                },
                cellClassName: (params) => getEditabilityForField('recommendedRetailPrice') ? 'editableDistributionCell' : 'nonEditableCell',
            },
            {
                field: 'unitFunding',
                headerName: selectedPromotion.promotionTypeId === 3 ? "Promotional Cost Price" : "Unit Funding",
                type: 'number',
                width: 100,
                editable: false, // Disable default editing since we handle it in custom cell
                sortable: false,
                headerAlign: 'center',
                renderCell: (params) => (
                    <UnitFundingCell 
                        params={params} 
                        isEditable={getEditabilityForField('unitFunding')}
                        theme={theme}
                        isForecastPeriodLocked={isForecastPeriodLocked}
                    />
                ),
                valueGetter: (value, row) => {
                    // Get current value including any unsaved edits
                    const editedRow = editedPromotionProductRows[row.promotionProductId];
                    return editedRow ? editedRow.unitFunding : row.unitFunding;
                },
                cellClassName: (params) => getEditabilityForField('unitFunding') ? 'editableDistributionCell' : 'nonEditableCell',
            }
        ];

        let promotionProducts = selectedPromotion?.promotionProducts;

        const dynamicColumns = promotionProducts.length && promotionProducts[0]?.weeklyDistributionRates
            ? promotionProducts[0].weeklyDistributionRates.map((wdr) => {
                const weekPosition = getWeekPosition(wdr.week);
                return {
                    field: wdr.week,
                    headerName: formatWeekHeader(wdr.week, weekPosition),
                    width: 110, // Slightly increased width for side-by-side layout
                    editable: false, // Disable default editing since we handle it in custom cell
                    sortable: false,
                    headerAlign: 'center',
                    renderCell: (params) => (
                        <DistributionRateCell 
                            params={params} 
                            isEditable={getEditabilityForField(wdr.week)}
                            theme={theme}
                            isForecastPeriodLocked={isForecastPeriodLocked}
                        />
                    ),
                    valueGetter: (value, row) => {
                        const weekData = row.weeklyDistributionRates.find(w => w.week === wdr.week);
                        const cellKey = `${row.promotionProductId}-${wdr.week}`;
                        
                        // Check if cell has unsaved changes
                        const hasUnsavedChanges = unsavedCellChanges.has(cellKey);
                        
                        if (hasUnsavedChanges) {
                            // For unsaved changes, calculate from local state
                            const editedRow = editedPromotionProductRows[row.promotionProductId];
                            const editedWeekData = editedRow?.weeklyDistributionRates?.find(w => w.week === wdr.week);
                            const baseValue = editedWeekData?.basePromoDistributionRate || weekData?.basePromoDistributionRate || 0;
                            const percentUplift = percentUpliftValues[cellKey] || 0;
                            return Math.round(baseValue * (1 + percentUplift / 100));
                        } else {
                            // Use server-provided promoDistributionRate
                            return weekData?.promoDistributionRate || weekData?.basePromoDistributionRate || 0;
                        }
                    },
                    cellClassName: (params) => getEditabilityForField(wdr.week) ? 'editableDistributionCell' : 'nonEditableCell',
                };
            })
            : [];

        return [...staticColumns, ...dynamicColumns];
    }, [selectedPromotion, percentUpliftValues, unsavedCellChanges, theme, isForecastPeriodLocked, getEditabilityForField]);

    const handleCreatePromotionProductClick = () => {
        setOpenPromotionProductDialog(true);
        setPromotionProductFormData({
            recommendedRetailPrice: 0,
            unitFunding: 0,
            skuBaseForecastId: []
        });
        setSkuBaseForecastErrorText('');
        setRecommendedRetailPriceErrorText('');
        setUnitFundingErrorText('');
    };

    const handleClose = () => {
        setOpenPromotionProductDialog(false);
    };

    const handleProductsSelected = (products) => {
        setPromotionProductFormData((prevState) => ({
            ...prevState,
            skuBaseForecastId: products.map(product => product.skuBaseForecastId),
        }));
    };

    const handleTextFieldChange = (event) => {
        const {name, value} = event.target;

        setPromotionProductFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const isCellEditable = useCallback((cell) => {
        return getEditabilityForField(cell.field);
    }, [getEditabilityForField]);

    const handleCellSelectionModelChange = useCallback(() => {
        const selected = apiRef.current.getSelectedCellsAsArray();
        const editableCells = selected.filter(cell => isCellEditable(cell));
        setSelectedCells(editableCells);
    }, [apiRef, isCellEditable]);


    // Custom cell renderer for RRP column
    const RRPCell = useCallback(({ params, isEditable, theme, isForecastPeriodLocked }) => {
        const { row } = params;
        
        // Get current value including any unsaved edits
        const editedRow = editedPromotionProductRows[row.promotionProductId];
        const currentValue = editedRow ? editedRow.recommendedRetailPrice : row.recommendedRetailPrice;
        const originalValue = row.recommendedRetailPrice;
        
        // Check if cell has unsaved changes
        const cellKey = `${row.promotionProductId}-rrp`;
        const hasUnsavedChanges = unsavedCellChanges.has(cellKey);
        
        // Theme-aware colors
        const isDark = theme.palette.mode === 'dark';
        const isCellSelected = selectedCells.some(cell =>
            cell.id === params.id && cell.field === params.field
        );
        
        const handleCellDoubleClick = (e) => {
            e.stopPropagation();
            if (!isEditable || isForecastPeriodLocked) return;

            const selectedRRPCells = selectedCells.filter(cell =>
                cell.field === 'recommendedRetailPrice' && isCellEditable(cell)
            );
            const selectedUnitFundingCells = selectedCells.filter(cell =>
                cell.field === 'unitFunding' && isCellEditable(cell)
            );
            const isCurrentCellSelected = selectedRRPCells.some(cell =>
                cell.id === params.id && cell.field === params.field
            );

            if (selectedRRPCells.length > 1 && selectedUnitFundingCells.length === 0 && isCurrentCellSelected) {
                setRRPEditData({
                    selectedCells: selectedRRPCells,
                    currentValue
                });
                setOpenRRPEditDialog(true);
                return;
            }
            
            // Open edit dialog for RRP
            setRRPEditData({
                row,
                currentValue,
                originalValue,
                cellKey
            });
            setOpenRRPEditDialog(true);
        };

        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    cursor: isEditable && !isForecastPeriodLocked ? 'pointer' : 'default',
                    backgroundColor: isCellSelected
                        ? (isDark ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)')
                        : 'transparent',
                    '&:hover': {
                        backgroundColor: isEditable && !isForecastPeriodLocked 
                            ? (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)')
                            : 'transparent',
                    },
                }}
                onDoubleClick={handleCellDoubleClick}
                data-testid={`rrp-cell-${row.promotionProductId}`}
            >
                {/* Status Indicator */}
                {hasUnsavedChanges && (
                    <Box sx={{ position: 'absolute', top: 2, right: 2 }}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.warning.main,
                                boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                            }}
                        />
                    </Box>
                )}
                
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: hasUnsavedChanges ? 600 : 400,
                        color: hasUnsavedChanges 
                            ? theme.palette.text.primary
                            : (!isEditable || isForecastPeriodLocked)
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                    }}
                >
                    {currentValue !== null && currentValue !== undefined ? `£${parseFloat(currentValue).toFixed(2)}` : '£0.00'}
                </Typography>
            </Box>
        );
    }, [editedPromotionProductRows, unsavedCellChanges, theme, selectedCells, isCellEditable]);

    // Custom cell renderer for Unit Funding column
    const UnitFundingCell = useCallback(({ params, isEditable, theme, isForecastPeriodLocked }) => {
        const { row } = params;
        
        // Get current value including any unsaved edits
        const editedRow = editedPromotionProductRows[row.promotionProductId];
        const currentValue = editedRow ? editedRow.unitFunding : row.unitFunding;
        const originalValue = row.unitFunding;
        
        // Check if cell has unsaved changes
        const cellKey = `${row.promotionProductId}-unitfunding`;
        const hasUnsavedChanges = unsavedCellChanges.has(cellKey);
        
        // Theme-aware colors
        const isDark = theme.palette.mode === 'dark';
        const isCellSelected = selectedCells.some(cell =>
            cell.id === params.id && cell.field === params.field
        );
        
        const handleCellDoubleClick = (e) => {
            e.stopPropagation();
            if (!isEditable || isForecastPeriodLocked) return;

            const selectedUnitFundingCells = selectedCells.filter(cell =>
                cell.field === 'unitFunding' && isCellEditable(cell)
            );
            const selectedRRPCells = selectedCells.filter(cell =>
                cell.field === 'recommendedRetailPrice' && isCellEditable(cell)
            );
            const isCurrentCellSelected = selectedUnitFundingCells.some(cell =>
                cell.id === params.id && cell.field === params.field
            );

            if (selectedUnitFundingCells.length > 1 && selectedRRPCells.length === 0 && isCurrentCellSelected) {
                setUnitFundingEditData({
                    selectedCells: selectedUnitFundingCells,
                    currentValue,
                    headerName: selectedPromotion?.promotionTypeId === 3 ? "Promotional Cost Price" : "Unit Funding"
                });
                setOpenUnitFundingEditDialog(true);
                return;
            }
            
            // Open edit dialog for Unit Funding
            setUnitFundingEditData({
                row,
                currentValue,
                originalValue,
                cellKey
            });
            setOpenUnitFundingEditDialog(true);
        };

        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    cursor: isEditable && !isForecastPeriodLocked ? 'pointer' : 'default',
                    backgroundColor: isCellSelected
                        ? (isDark ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)')
                        : 'transparent',
                    '&:hover': {
                        backgroundColor: isEditable && !isForecastPeriodLocked 
                            ? (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)')
                            : 'transparent',
                    },
                }}
                onDoubleClick={handleCellDoubleClick}
                data-testid={`unit-funding-cell-${row.promotionProductId}`}
            >
                {/* Status Indicator */}
                {hasUnsavedChanges && (
                    <Box sx={{ position: 'absolute', top: 2, right: 2 }}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.warning.main,
                                boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                            }}
                        />
                    </Box>
                )}
                
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: hasUnsavedChanges ? 600 : 400,
                        color: hasUnsavedChanges 
                            ? theme.palette.text.primary
                            : (!isEditable || isForecastPeriodLocked)
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                    }}
                >
                    {currentValue !== null && currentValue !== undefined ? `£${parseFloat(currentValue).toFixed(2)}` : '£0.00'}
                </Typography>
            </Box>
        );
    }, [editedPromotionProductRows, unsavedCellChanges, theme, selectedCells, isCellEditable, selectedPromotion]);

    // Custom cell renderer for distribution rate columns
    const DistributionRateCell = useCallback(({ params, isEditable, theme, isForecastPeriodLocked }) => {
        const { row, field } = params;
        const weekData = row.weeklyDistributionRates.find(w => w.week === field);
        
        // Get base promo distribution rate from server (can be NULL, undefined, or a number)
        // NULL means "use base rate from grid", undefined means no data yet, number means locked rate
        const basePromoDistributionRate = weekData?.basePromoDistributionRate;
        
        // Get current base value including any unsaved edits
        const editedRow = editedPromotionProductRows[row.promotionProductId];
        
        // The base value to display (this is the base promo distribution rate before uplift)
        // Can be NULL if user has chosen to use base rate from grid
        let baseValueOverride;
        if (editedRow) {
            // Use edited value if available (basePromoDistributionRate stores the user's entered base rate or NULL)
            const editedWeekData = editedRow.weeklyDistributionRates?.find(w => w.week === field);
            baseValueOverride = editedWeekData?.basePromoDistributionRate !== undefined ? editedWeekData.basePromoDistributionRate : basePromoDistributionRate;
        } else {
            baseValueOverride = basePromoDistributionRate;
        }
        
        // Get percent uplift for this specific cell
        const cellKey = `${row.promotionProductId}-${field}`;
        
        // Check if cell has unsaved changes
        const hasUnsavedChanges = unsavedCellChanges.has(cellKey);
        
        // The actual base value to use for calculations and display
        // NULL/undefined means use original base rate from grid
        const baseValue = (baseValueOverride !== null && baseValueOverride !== undefined) 
            ? baseValueOverride 
            : (weekData?.originalBaseDistributionRate || 0);
        
        // Track if rate is overridden (locked to a specific value, not following base grid)
        // Use the server's hasPromoRateOverride flag for saved data, or check local edits
        const isRateOverridden = hasUnsavedChanges 
            ? (baseValueOverride !== null && baseValueOverride !== undefined)
            : (weekData?.hasPromoRateOverride || false);
        
        // Use backend uplift data first, then fall back to local state (for unsaved changes)
        let percentUplift = 0;
        if (hasUnsavedChanges && cellKey in percentUpliftValues) {
            // Use local state for unsaved changes (can be NULL which means no uplift)
            percentUplift = percentUpliftValues[cellKey] ?? 0;
        } else if (weekData?.upliftPercent !== null && weekData?.upliftPercent !== undefined) {
            // Use backend data for saved values
            percentUplift = weekData.upliftPercent;
        }
        
        // Get effective value (promo distribution rate)
        let effectiveValue;
        if (hasUnsavedChanges) {
            // Calculate for unsaved changes
            effectiveValue = Math.round(baseValue * (1 + percentUplift / 100));
        } else {
            // Use server-provided promoDistributionRate
            effectiveValue = weekData?.promoDistributionRate || baseValue;
        }
        
        // Check if cell has actual saved promo overrides from server
        const weekDataForCell = row.weeklyDistributionRates.find(w => w.week === field);
        const hasActualOverrides = weekDataForCell?.hasPromoOverride || false;
        
        // Theme-aware colors
        const isDark = theme.palette.mode === 'dark';
        
        const handleCellDoubleClick = (e) => {
            e.stopPropagation();
            if (!isEditable || isForecastPeriodLocked) return;
            
            // Check if multiple cells are currently selected for bulk editing
            // Only consider cells that are actually selected in the current context and are editable
            const currentDistributionRateCells = selectedCells.filter(cell => {
                // Check if the cell field matches date pattern (DD-MM-YYYY) and is editable
                return /^\d{2}-\d{2}-\d{4}$/.test(cell.field) && isCellEditable(cell);
            });
            
            // Only open bulk dialog if we have multiple cells selected AND
            // the current cell being double-clicked is part of that selection
            const isCurrentCellSelected = currentDistributionRateCells.some(cell => 
                cell.id === params.id && cell.field === params.field
            );
            
            if (currentDistributionRateCells.length > 1 && isCurrentCellSelected) {
                // Open bulk edit dialog
                setBulkDistributionEditData({
                    selectedCells: currentDistributionRateCells
                });
                setOpenBulkDistributionEditDialog(true);
                return;
            }
            
            // Single cell edit (existing logic)
            // Get current values including any unsaved edits
            const editedRow = editedPromotionProductRows[row.promotionProductId];
            const currentBaseValue = editedRow ? 
                editedRow.weeklyDistributionRates?.find(w => w.week === field)?.actualDistributionRate || baseValue :
                baseValue;
            
            // Get the uplift percent using the same logic as the cell display
            const weekDataForDialog = row.weeklyDistributionRates.find(w => w.week === field);
            let currentUpliftPercent = 0;
            if (hasUnsavedChanges && percentUpliftValues[cellKey] !== undefined) {
                // Use local state for unsaved changes
                currentUpliftPercent = percentUpliftValues[cellKey];
            } else if (weekDataForDialog?.upliftPercent !== null && weekDataForDialog?.upliftPercent !== undefined) {
                // Use backend data for saved values
                currentUpliftPercent = weekDataForDialog.upliftPercent;
            }
            
            // Check if rate is overridden or using base
            const basePromoDistributionRateRaw = weekDataForDialog?.basePromoDistributionRate;
            const isUsingBaseRate = !weekDataForDialog?.hasPromoRateOverride;
            
            // For display, use the actual value (which may be computed from base)
            const displayBaseValue = basePromoDistributionRateRaw !== null && basePromoDistributionRateRaw !== undefined
                ? basePromoDistributionRateRaw
                : (weekDataForDialog?.originalBaseDistributionRate || 0);
            
            const currentEffectiveValue = Math.round(displayBaseValue * (1 + currentUpliftPercent / 100));
            
            // Open the modal with all the context
            setDistributionEditData({
                row,
                field,
                baseValue: displayBaseValue,
                percentUplift: currentUpliftPercent,
                effectiveValue: currentEffectiveValue,
                cellKey,
                productName: row.skuDescription,
                weekDate: field,
                useBaseRate: isUsingBaseRate
            });
            setOpenDistributionEditDialog(true);
        };
        
        // Check if this cell is selected
        const isCellSelected = selectedCells.some(cell => 
            cell.id === params.id && cell.field === params.field
        );

        return (
            <Box 
                    sx={{ 
                        p: 0.5, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                        minHeight: '52px',
                        gap: 0.1,
                        borderRadius: '4px',
                        position: 'relative',
                        backgroundColor: isCellSelected 
                            ? (isDark ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)')
                            : 'transparent',
                        border: 'none',
                        '&:hover': isEditable && !isCellSelected ? {
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                        } : {},
                        transition: 'all 0.2s ease',
                        cursor: isEditable ? 'pointer' : 'default',
                        pointerEvents: 'auto' // Ensure pointer events work for double-click
                    }}
                    onDoubleClick={handleCellDoubleClick}
                >
                    {/* Status Indicators */}
                    {(hasUnsavedChanges || hasActualOverrides) && (
                        <Box sx={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 0.25 }}>
                            {hasUnsavedChanges && (
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: theme.palette.warning.main,
                                        boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                                    }}
                                    title="Unsaved changes"
                                />
                            )}
                            {hasActualOverrides && !hasUnsavedChanges && (
                                <Box
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isEditable) return;
                                        setResetDialog({
                                            open: true,
                                            promotionProductId: row.promotionProductId,
                                            week: field,
                                            currentValue: effectiveValue,
                                            baseValue: weekData?.originalBaseDistributionRate || 0
                                        });
                                    }}
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        // Blue for rate override, green for uplift only
                                        backgroundColor: isRateOverridden ? theme.palette.primary.main : theme.palette.success.main,
                                        boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                                        cursor: isEditable ? 'pointer' : 'default',
                                    }}
                                    title={isRateOverridden ? "Rate is overridden (locked) - click to reset" : "Uplift only (rate follows base) - click to reset"}
                                />
                            )}
                        </Box>
                    )}
                    {/* Base Promo Distribution Rate */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: percentUplift > 0 ? '12px' : '14px',
                            fontWeight: isRateOverridden ? 700 : 500,  // Bold if overridden
                            color: !isEditable 
                                ? theme.palette.text.disabled 
                                : isRateOverridden
                                    ? theme.palette.primary.main  // Blue if rate is locked/overridden
                                    : (percentUplift > 0 ? theme.palette.text.secondary : theme.palette.text.primary),
                            lineHeight: 1.2,
                        }}
                    >
                        {baseValue.toLocaleString()}
                    </Typography>
                    
                    {/* Percent Uplift - on new line */}
                    {percentUplift > 0 && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '12px',
                                color: !isEditable 
                                    ? theme.palette.text.disabled 
                                    : theme.palette.success.main, // Always green since uplift should always be positive
                                lineHeight: 1.2,
                            }}
                        >
                            +{percentUplift}%
                        </Typography>
                    )}
                    
                    {/* Promo Distribution Rate - on new line */}
                    {percentUplift > 0 && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '14px',
                                color: !isEditable 
                                    ? theme.palette.text.disabled 
                                    : theme.palette.text.primary,
                                lineHeight: 1.2,
                            }}
                        >
                            {effectiveValue.toLocaleString()}
                        </Typography>
                    )}
                </Box>
        );
    }, [theme, editedPromotionProductRows, percentUpliftValues, unsavedCellChanges, setDistributionEditData, setOpenDistributionEditDialog, isForecastPeriodLocked, isCellEditable, selectedCells]);

    const handleCreatePromoProduct = async (data) => {
        if (isCreatingPromotionProductRef.current) {
            return;
        }
        const isValidRecommendedRetailPrice = isMoneyGreaterThanZero(data.recommendedRetailPrice);
        const isValidUnitFunding = isMoneyGreaterThanZero(data.unitFunding);

        const isValidSkuBaseForecastId = data.skuBaseForecastId.length > 0;

        if (!isValidRecommendedRetailPrice || !isValidUnitFunding || !isValidSkuBaseForecastId) {
            setRecommendedRetailPriceErrorText(isValidRecommendedRetailPrice ? '' : 'Please enter a valid number with up to two decimal places and greater than 0');
            setUnitFundingErrorText(isValidUnitFunding ? '' : 'Please enter a valid number with up to two decimal places and greater than 0');
            setSkuBaseForecastErrorText(isValidSkuBaseForecastId ? '' : 'Please select a SKU');
            return;
        }

        setRecommendedRetailPriceErrorText('');
        setUnitFundingErrorText('');
        setSkuBaseForecastErrorText('');

        try {
            isCreatingPromotionProductRef.current = true;
            setLoadingPromotionProducts(true);
            data.customerId = customerId;
            data.forecastPeriodId = forecastPeriodId;

            const requests = data.skuBaseForecastId.map(async (skuBaseForecastId) => {
                const promotionData = { ...data, skuBaseForecastId };
                try {
                    await axiosInstance.post(`/forecasts/${forecastId}/promotions/${selectedPromotionId}/promotionProducts`, promotionData);
                    return { skuBaseForecastId, status: 'success' };
                } catch (error) {
                    const errorMessage = error.response?.data || error.message || 'Unknown error';
                    return { skuBaseForecastId, status: 'failed', errorMessage };
                }
            });

            const results = await Promise.all(requests);

            const successful = results.filter(r => r.status === 'success').map(r => r.skuBaseForecastId);
            const failed = results.filter(r => r.status === 'failed');
            
            if (successful.length > 0 && failed.length === 0) {
                const newPromotionProducts = successful.map(skuBaseForecastId => ({
                    skuBaseForecastId,
                    weeklyDistributionRates: selectedPromotion?.promotionProducts?.length > 0
                        ? selectedPromotion.promotionProducts[0].weeklyDistributionRates.map(rate => rate.week)
                        : []
                }));

                const updatedPromotionProductsData = [...promotionProductsData, ...newPromotionProducts];

                setPromotionProductsData(updatedPromotionProductsData);
                onPromotionProductsDataChange(updatedPromotionProductsData);

                setSnackbar({
                    children: 'Promotion products created successfully.',
                    severity: 'success',
                });
            } else if (failed.length > 0 && successful.length === 0) {
                const errorMessages = failed
                    .map(f => `SKU Customer Forecast ID: ${f.skuBaseForecastId} - ${f.errorMessage}`)
                    .join('\n');
                setSnackbar({
                    children: `Failed to create promotion products:\n${errorMessages}`,
                    severity: 'error',
                });
            } else {
                const errorMessages = failed
                    .map(f => `SKU Customer Forecast ID: ${f.skuBaseForecastId} - ${f.errorMessage}`)
                    .join('\n');

                setSnackbar({
                    children: `Failed to create the following promotion products:\n${errorMessages}`,
                    severity: 'error',
                });
            }

            // Close the modal if at least one was successful
            if (successful.length > 0) {
                handleClose();
                try {
                    await onRefreshPromotions();
                } catch (refreshError) {
                    console.error('Error refreshing promotions:', refreshError);
                }
            }
        } catch (error) {
            console.error('Error creating promotion products:', error);
            setSnackbar({
                children: 'Unexpected error while processing promotion products.',
                severity: 'error',
            });
        } finally {
            try {
                onRefreshSkuBaseForecasts();
            } catch (refreshError) {
                console.error('Error refreshing SKU base forecasts:', refreshError);
            }
            setLoadingPromotionProducts(false);
            isCreatingPromotionProductRef.current = false;
        }
    };

    const handlePromotionProductRowClick = (params) => {
        setSelectedPromotionProductId(params.id);
        setSelectedPromotionProduct(params.row);
    };


    // Helper function to get week data efficiently
    const getWeekData = (weeklyDistributionRates, week) => {
        return weeklyDistributionRates.find(wdr => wdr.week === week);
    };

    // Helper function to determine the actual distribution rate for a week
    // Returns NULL if using base rate, or the override value
    const getActualDistributionRate = (editedRow, originalRow, week) => {
        const editedWeekData = getWeekData(editedRow.weeklyDistributionRates, week);
        
        // If basePromoDistributionRate is explicitly set (including NULL), use it
        if (editedWeekData && 'basePromoDistributionRate' in editedWeekData) {
            return editedWeekData.basePromoDistributionRate; // Can be NULL
        }
        
        // Otherwise fall back to original
        const originalWeekData = getWeekData(originalRow.weeklyDistributionRates, week);
        return originalWeekData?.basePromoDistributionRate;
    };

    // Helper function to determine uplift percent
    // Returns NULL if no uplift, or the uplift value
    const getUpliftPercent = (editedRow, originalRow, week) => {
        const cellKey = `${editedRow.promotionProductId}-${week}`;
        
        // Check if there's a value in percentUpliftValues (can be NULL)
        if (cellKey in percentUpliftValues) {
            return percentUpliftValues[cellKey]; // Can be NULL
        }
        
        // Fall back to original value
        const originalWeekData = getWeekData(originalRow.weeklyDistributionRates, week);
        return originalWeekData?.upliftPercent ?? null;
    };

    const transformEditedRowToOverrideRequest = (editedRow, originalRow, forecastPeriodId) => {
        // Get weeks with distribution rate changes
        const changedRates = editedRow !== originalRow 
            ? getChangedWeeklyDistributionRates(editedRow.weeklyDistributionRates, originalRow.weeklyDistributionRates)
            : [];

        // Get weeks with uplift changes (unsaved changes)
        const weeksWithUpliftChanges = originalRow.weeklyDistributionRates
            .filter(wdr => unsavedCellChanges.has(`${editedRow.promotionProductId}-${wdr.week}`))
            .map(wdr => ({
                week: wdr.week,
                actualDistributionRate: getActualDistributionRate(editedRow, originalRow, wdr.week)
            }));

        // Combine all changes, prioritizing rate changes over uplift-only changes
        const allChangesMap = new Map();
        weeksWithUpliftChanges.forEach(change => allChangesMap.set(change.week, change));
        changedRates.forEach(change => allChangesMap.set(change.week, change));
        
        // Create overrides for all changed weeks
        const promotionDistributionRateOverrides = Array.from(allChangesMap.values()).map(change => ({
            PromotionProductId: editedRow.promotionProductId,
            WeekStart: change.week,
            PromoDistributionRate: change.actualDistributionRate === '' ? null : change.actualDistributionRate,
            UpliftPercent: getUpliftPercent(editedRow, originalRow, change.week),
            ForecastPeriodId: forecastPeriodId
        }));

        // Handle promotion product overrides (RRP and Unit Funding changes)
        const promotionProductOverrides = editedRow !== originalRow && 
            (editedRow.unitFunding !== originalRow.unitFunding || editedRow.recommendedRetailPrice !== originalRow.recommendedRetailPrice)
            ? {
                PromotionProductId: editedRow.promotionProductId,
                UnitFunding: parseFloat(editedRow.unitFunding || 0),
                RecommendedRetailPrice: parseFloat(editedRow.recommendedRetailPrice || 0),
                ForecastPeriodId: forecastPeriodId
            }
            : null;

        return {
            PromotionDistributionRateOverrides: promotionDistributionRateOverrides,
            PromotionProductOverrides: promotionProductOverrides
        };
    };


    const handleSavePromotionProductClick = async () => {
        try {
            setLoadingPromotionProducts(true);
            const rowsArray = Object.values(editedPromotionProductRows);
            
            // Also handle rows that only have unsaved cell changes (like percent uplift only)
            const rowsWithUnsavedChanges = Array.from(unsavedCellChanges).map(cellKey => {
                const [promotionProductId] = cellKey.split('-');
                return parseInt(promotionProductId);
            }).filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates
            
            // Combine edited rows with rows that have unsaved changes
            const allAffectedRows = new Set([
                ...rowsArray.map(row => row.promotionProductId),
                ...rowsWithUnsavedChanges
            ]);

            const transformedRows = Array.from(allAffectedRows).map(promotionProductId => {
                const editedRow = editedPromotionProductRows[promotionProductId];
                const originalRow = selectedPromotion.promotionProducts.find(row => row.promotionProductId === promotionProductId);
                
                // Use edited row if it exists, otherwise use original row for percent uplift only changes
                const rowToTransform = editedRow || originalRow;
                return transformEditedRowToOverrideRequest(rowToTransform, originalRow, forecastPeriodId);
            });

            // Validate distribution rates are whole numbers (>= 0)
            const allOverridesForValidation = [].concat(...transformedRows.map(r => r.PromotionDistributionRateOverrides));
            const hasInvalidDistributionRate = allOverridesForValidation.some(o => {
                const value = o.PromoDistributionRate;
                if (value === null || value === undefined) return false; // null indicates deletion/reset
                return !isGreaterOrEqualToZero(value) || !Number.isInteger(value);
            });

            if (hasInvalidDistributionRate) {
                setSnackbar({
                    children: 'Distribution rates must be whole numbers (0 or greater). Please correct them before saving.',
                    severity: 'error'
                });
                setLoadingPromotionProducts(false);
                return;
            }

            const payload = {
                PromotionDistributionRateOverrides: [].concat(...transformedRows.map(row => row.PromotionDistributionRateOverrides)),
                PromotionProductOverrides: transformedRows.map(row => row.PromotionProductOverrides).filter(override => override !== null),
                forecastPeriodId: forecastPeriodId
            };
            
            const response = await axiosInstance.post(`/forecasts/${forecastId}/promotions/${selectedPromotionId}/promotionProducts/bulk`, payload);

            if (response.status === 200) {
                setSnackbar({children: "Changes saved successfully", severity: "success"});
                setEditedPromotionProductRows({});
                
                // Clear percent uplift values for saved changes
                const savedCellKeys = Array.from(unsavedCellChanges);
                setPercentUpliftValues(prev => {
                    const updated = { ...prev };
                    savedCellKeys.forEach(key => {
                        delete updated[key];
                    });
                    return updated;
                });
                
                setUnsavedCellChanges(new Set()); // Clear unsaved changes after using them
                
                // Clear cell selection to remove blue borders
                setSelectedCells([]);
                
                try {
                    await onRefreshPromotions();
                } catch (refreshError) {
                    console.error('Error refreshing promotions:', refreshError);
                }
            } else {
                setSnackbar({children: "Failed to save changes", severity: "error"});
            }
        } catch (error) {
            const errorMessage =
                (error && error.response && error.response.data)
                    ? error.response.data
                    : (error && error.message)
                        ? error.message
                        : "An unknown error occurred";
            console.error(error);
            setSnackbar({children: `Error saving changes: ${errorMessage}`, severity: "error"});
        } finally {
            try {
                onRefreshSkuBaseForecasts();
            } catch (refreshError) {
                console.error('Error refreshing SKU base forecasts:', refreshError);
            }
            setLoadingPromotionProducts(false);
        }
    }

    const handleBulkDistributionSave = (updatedData) => {
        const { baseValue, percentUplift, selectedCells } = updatedData;
        
        // baseValue can be NULL (use base rate) or a number (override)
        // percentUplift can be NULL (no uplift) or a number
        
        // Group selected cells by promotion product ID
        const cellsByPromotionProduct = selectedCells.reduce((acc, cell) => {
            if (!acc[cell.id]) {
                acc[cell.id] = [];
            }
            acc[cell.id].push(cell.field);
            return acc;
        }, {});
        
        // Apply changes to each affected promotion product
        Object.entries(cellsByPromotionProduct).forEach(([promotionProductId, weeks]) => {
            const promotionProductIdNum = parseInt(promotionProductId);
            const originalRow = selectedPromotion.promotionProducts.find(
                row => row.promotionProductId === promotionProductIdNum
            );
            
            if (!originalRow) return;
            
            // Get or create edited row
            let editedRow = editedPromotionProductRows[promotionProductIdNum];
            if (!editedRow) {
                editedRow = JSON.parse(JSON.stringify(originalRow));
            }
            
            // Apply base value to all selected weeks (can be NULL)
            weeks.forEach(week => {
                const weekIndex = editedRow.weeklyDistributionRates.findIndex(w => w.week === week);
                if (weekIndex !== -1) {
                    editedRow.weeklyDistributionRates[weekIndex].basePromoDistributionRate = baseValue;
                }
            });
            
            // Update edited rows for this promotion product
            setEditedPromotionProductRows(prev => ({
                ...prev,
                [promotionProductIdNum]: editedRow
            }));
        });
        
        // Apply percent uplift values and mark all affected cells as having unsaved changes
        selectedCells.forEach(cell => {
            const cellKey = `${cell.id}-${cell.field}`;
            
            // Set percent uplift (can be NULL)
            setPercentUpliftValues(prev => ({
                ...prev,
                [cellKey]: percentUplift
            }));
            
            // Mark cell as having unsaved changes
            setUnsavedCellChanges(prev => new Set([...prev, cellKey]));
        });
        
        // Clear cell selection to remove blue borders after bulk editing
        setSelectedCells([]);
        
        setOpenBulkDistributionEditDialog(false);
    };

    const handleBulkRRPSave = (updatedData) => {
        const { value, selectedCells } = updatedData;

        setEditedPromotionProductRows(prev => {
            const next = { ...prev };
            const rowIds = [...new Set(selectedCells.map(cell => Number(cell.id)))];

            rowIds.forEach((promotionProductIdNum) => {
                const originalRow = selectedPromotion.promotionProducts.find(
                    row => row.promotionProductId === promotionProductIdNum
                );
                if (!originalRow) return;

                const existingEditedRow = next[promotionProductIdNum];
                const editedRow = existingEditedRow
                    ? { ...existingEditedRow }
                    : JSON.parse(JSON.stringify(originalRow));

                editedRow.recommendedRetailPrice = value;
                next[promotionProductIdNum] = editedRow;
            });

            return next;
        });

        setUnsavedCellChanges(prev => {
            const next = new Set(prev);
            selectedCells.forEach((cell) => {
                const cellKey = `${cell.id}-rrp`;
                const originalRow = selectedPromotion.promotionProducts.find(
                    row => row.promotionProductId === Number(cell.id)
                );
                if (originalRow && value !== originalRow.recommendedRetailPrice) {
                    next.add(cellKey);
                } else {
                    next.delete(cellKey);
                }
            });
            return next;
        });

        setSelectedCells([]);
        setOpenRRPEditDialog(false);
    };

    const handleBulkUnitFundingSave = (updatedData) => {
        const { value, selectedCells } = updatedData;

        setEditedPromotionProductRows(prev => {
            const next = { ...prev };
            const rowIds = [...new Set(selectedCells.map(cell => Number(cell.id)))];

            rowIds.forEach((promotionProductIdNum) => {
                const originalRow = selectedPromotion.promotionProducts.find(
                    row => row.promotionProductId === promotionProductIdNum
                );
                if (!originalRow) return;

                const existingEditedRow = next[promotionProductIdNum];
                const editedRow = existingEditedRow
                    ? { ...existingEditedRow }
                    : JSON.parse(JSON.stringify(originalRow));

                editedRow.unitFunding = value;
                next[promotionProductIdNum] = editedRow;
            });

            return next;
        });

        setUnsavedCellChanges(prev => {
            const next = new Set(prev);
            selectedCells.forEach((cell) => {
                const cellKey = `${cell.id}-unitfunding`;
                const originalRow = selectedPromotion.promotionProducts.find(
                    row => row.promotionProductId === Number(cell.id)
                );
                if (originalRow && value !== originalRow.unitFunding) {
                    next.add(cellKey);
                } else {
                    next.delete(cellKey);
                }
            });
            return next;
        });

        setSelectedCells([]);
        setOpenUnitFundingEditDialog(false);
    };

    const PromoProductEditToolbar = ({ title }) => {
        const handleDeletePromotionProduct = () => {
            if (!selectedPromotionProduct) {
                setSnackbar({ children: "No promotion product selected", severity: "error" });
                return;
            }
            handleOpenConfirmationDialog('promotionProduct');
        };

        const handleBulkEditClick = () => {
            const editableDistributionRateCells = selectedCells.filter(cell => {
                return /^\d{2}-\d{2}-\d{4}$/.test(cell.field) && isCellEditable(cell);
            });
            const editableRRPCells = selectedCells.filter(cell => {
                return cell.field === 'recommendedRetailPrice' && isCellEditable(cell);
            });
            const editableUnitFundingCells = selectedCells.filter(cell => {
                return cell.field === 'unitFunding' && isCellEditable(cell);
            });
            
            if (editableDistributionRateCells.length > 1) {
                setBulkDistributionEditData({
                    selectedCells: editableDistributionRateCells
                });
                setOpenBulkDistributionEditDialog(true);
            } else if (editableRRPCells.length > 1) {
                setRRPEditData({
                    selectedCells: editableRRPCells,
                    currentValue: null
                });
                setOpenRRPEditDialog(true);
            } else if (editableUnitFundingCells.length > 1) {
                setUnitFundingEditData({
                    selectedCells: editableUnitFundingCells,
                    currentValue: null,
                    headerName: selectedPromotion?.promotionTypeId === 3 ? "Promotional Cost Price" : "Unit Funding"
                });
                setOpenUnitFundingEditDialog(true);
            }
        };

        // Check if multiple editable distribution rate cells are selected
        const distributionRateCells = selectedCells.filter(cell => {
            return /^\d{2}-\d{2}-\d{4}$/.test(cell.field) && isCellEditable(cell);
        });
        const rrpCells = selectedCells.filter(cell => {
            return cell.field === 'recommendedRetailPrice' && isCellEditable(cell);
        });
        const unitFundingCells = selectedCells.filter(cell => {
            return cell.field === 'unitFunding' && isCellEditable(cell);
        });
        const selectedColumnTypes = [distributionRateCells, rrpCells, unitFundingCells].filter(cells => cells.length > 0).length;
        const hasSingleColumnSelection = selectedColumnTypes === 1;
        const bulkEditableCells = distributionRateCells.length > 1
            ? distributionRateCells
            : rrpCells.length > 1
                ? rrpCells
                : unitFundingCells.length > 1
                    ? unitFundingCells
                    : [];
        const showBulkEditButton = hasSingleColumnSelection && bulkEditableCells.length > 1;

        return (
            <GridToolbarContainer sx={{ justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ mr: 2, pl: 1 }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {showBulkEditButton && (
                        <Typography variant="body2" sx={{ 
                            color: theme.palette.primary.main, 
                            fontWeight: 600,
                            mr: 2
                        }}>
                            {bulkEditableCells.length} cells selected
                        </Typography>
                    )}
                    {showBulkEditButton && (
                        <Tooltip title="Bulk Edit Selected Cells">
                            <span>
                                <IconButton 
                                    aria-label="bulkEdit" 
                                    onClick={handleBulkEditClick}
                                    sx={{ 
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main + '20'
                                        }
                                    }}
                                >
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title="Create">
                        <span>
                            <IconButton aria-label="createPromotionProduct" onClick={handleCreatePromotionProductClick}>
                                <AddIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Save">
                        <span>
                            <IconButton
                                aria-label="save"
                                onClick={handleSavePromotionProductClick}
                                disabled={Object.keys(editedPromotionProductRows).length === 0 && unsavedCellChanges.size === 0}
                            >
                                <SaveIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <span>
                            <IconButton aria-label="delete" onClick={handleDeletePromotionProduct}>
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </GridToolbarContainer>
        );
    };

    return (
        <Box
            sx={{
                height: 500,
                width: '100%',
                '& .actions': {
                    color: 'text.secondary',
                },
                '& .textPrimary': {
                    color: 'text.primary',
                },
            }}
        >
            <DataGrid
                rows={rows}
                columns={columns}
                loading={loadingPromotions}
                rowSelectionModel={selectedPromotionId ? [selectedPromotionId] : []}
                onRowSelectionModelChange={handleRowSelection}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                }}
                editMode="row"
                onColumnResize={handleColumnResize}
                slots={{
                    toolbar: PromoEditToolbar,
                    loadingOverlay: LinearProgress,
                }}
                slotProps={{
                    toolbar: {
                        title: "Promotions",
                        onAdd: handleCreatePromotionDialog,
                        onEdit: handleEditPromotionDialog,
                        onDelete: handleDeleteClick,
                        onClone: handleClonePromotionDialog,
                        selectedPromotion,
                    },
                }}
                onRowClick={handlePromotionRowClick}
                pageSize={10}
                autoHeight
            />

            <Dialog open={openPromotionDialog} onClose={handleCloseDialogs} PaperComponent={PaperComponent}>
                <DialogTitle>Create New Promotion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {isEditMode ? "Update the details for the existing promotion." : "Enter the details for the new promotion."}
                    </DialogContentText>

                    {/* 1. Promotion Type Dropdown */}
                    <TextField
                        select
                        label="Promotion Type"
                        name="promotionTypeId"
                        fullWidth
                        value={promotionFormData.promotionTypeId || ''}
                        onChange={(e) => setPromotionFormData({ ...promotionFormData, promotionTypeId: parseInt(e.target.value) })}
                        sx={{ mt: 2 }}
                        error={Boolean(promotionTypeIdError)}
                        helperText={promotionTypeIdError}
                        disabled={isEditMode}
                    >
                        {promotionTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                                {type.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* 2. Name Text Field (Already Done) */}
                    <TextField
                        label="Name"
                        name="name"
                        fullWidth
                        value={promotionFormData.name}
                        onChange={(e) => setPromotionFormData({ ...promotionFormData, name: e.target.value })}
                        sx={{ mt: 2 }}
                        error={Boolean(nameError)}
                        helperText={nameError}
                    />

                    {/* 3. Buy In Date Range Picker */}
                    <Box sx={{ mt: 2 }}>
                        <WeekRestrictedDateRangePicker
                            buyInDateOptions={buyInDateOptions}
                            value={[promotionFormData.buyInStart, promotionFormData.buyInEnd]}
                            onChange={(newValue) => setPromotionFormData({
                                ...promotionFormData,
                                buyInStart: newValue[0],
                                buyInEnd: newValue[1]
                            })}
                            errorStartDate={buyInStartError}
                            errorEndDate={buyInEndError}
                            startLabel="Buy In Start"
                            endLabel="Buy In End"
                            disablePast={!allowPastWeeks}
                        />
                    </Box>

                    {/* 5. Fixed Spend Number Input */}
                    <TextField
                        label="Fixed Spend"
                        name="fixedSpend"
                        type="number"
                        fullWidth
                        value={promotionFormData.fixedSpend}
                        onChange={(e) => setPromotionFormData({ ...promotionFormData, fixedSpend: parseFloat(e.target.value) })}
                        sx={{ mt: 2 }}
                        error={Boolean(fixedSpendError)}
                        helperText={fixedSpendError}
                    />

                    {promotionFormData.promotionTypeId !== 2 && (
                        <>
                            <Box sx={{ mt: 1 }}>
                                <CustomDatePicker
                                    value={[
                                        promotionFormData.startDate ? dayjs(promotionFormData.startDate) : null,
                                        promotionFormData.endDate ? dayjs(promotionFormData.endDate) : null,
                                    ]}
                                    onChange={(newValue) => setPromotionFormData({
                                        ...promotionFormData,
                                        startDate: newValue[0]?.format("YYYY-MM-DD"),
                                        endDate: newValue[1]?.format("YYYY-MM-DD")
                                    })}
                                    errorStartDate={startDateError}
                                    errorEndDate={endDateError}
                                    startLabel="Promo Start"
                                    endLabel="Promo End"
                                />
                            </Box>


                            {/* 8. Features Dropdown */}
                            <TextField
                                select
                                label="Feature"
                                name="featureId"
                                fullWidth
                                value={promotionFormData.featureId || ''}
                                onChange={(e) => setPromotionFormData({ ...promotionFormData, featureId: parseInt(e.target.value) })}
                                sx={{ mt: 2 }}
                                error={Boolean(featureIdError)}
                                helperText={featureIdError}
                            >
                                {features.map((feature) => (
                                    <MenuItem key={feature.featureId} value={feature.featureId}>
                                        {feature.name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* 9. Stores Number Input */}
                            <TextField
                                label="Stores"
                                name="stores"
                                type="number"
                                fullWidth
                                value={promotionFormData.stores}
                                onChange={(e) => setPromotionFormData({ ...promotionFormData, stores: parseInt(e.target.value) })}
                                sx={{ mt: 2 }}
                                error={Boolean(storesError)}
                                helperText={storesError}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialogs} disabled={loadingPromotions}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleUpsertPromotion(promotionFormData)}
                        disabled={loadingPromotions}
                    >
                        {isEditMode ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>


            {selectedPromotion && selectedPromotion.promotionTypeId !== 2 && isGridReady && (
                <Box sx={{height: 'auto', width: '100%', mt: 4}}>
                    <DataGridPremium
                        apiRef={apiRef}
                        getRowId={(row) => row.promotionProductId}
                        rows={selectedPromotion.promotionProducts}
                        loading={loadingPromotionProducts}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    promotionProductId: false,
                                    skuBaseForecastId: false,
                                },
                            },
                        }}
                        columns={promotionProductColumns}
                        pageSize={5}
                        rowHeight={60} // Increased row height for the new cell layout
                        cellSelection
                        onCellSelectionModelChange={handleCellSelectionModelChange}
                        disableClipboardPaste
                        slots={{
                            toolbar: PromoProductEditToolbar,
                            loadingOverlay: LinearProgress,
                        }}
                        slotProps={{
                            toolbar: {title: "Promos - Sku Forecast"},
                        }}
                        onRowClick={handlePromotionProductRowClick}
                        sx={{
                            '& .editableDistributionCell': {
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.02)' 
                                    : 'rgba(0, 0, 0, 0.02)',
                                border: `1px solid ${theme.palette.divider}`,
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.05)' 
                                        : 'rgba(0, 0, 0, 0.04)',
                                },
                            },
                            '& .nonEditableCell': {
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.01)' 
                                    : 'rgba(0, 0, 0, 0.03)',
                                color: theme.palette.text.disabled,
                                border: `1px solid ${theme.palette.divider}`,
                            },
                            '& .leftAlignedCell': {
                                justifyContent: 'flex-start !important',
                                textAlign: 'left',
                            },
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px',
                                borderColor: theme.palette.divider,
                            },
                            '& .MuiDataGrid-columnHeader': {
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                borderColor: theme.palette.divider,
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                borderColor: theme.palette.divider,
                            },
                            '& .MuiDataGrid-row': {
                                borderColor: theme.palette.divider,
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.02)' 
                                        : 'rgba(0, 0, 0, 0.02)',
                                },
                            },
                        }}
                    />
                    
                    <Dialog open={openPromotionProductDialog} onClose={handleClose}>
                        <DialogTitle>Create Promotion Product</DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{pb: 2}}>
                                Enter the details for the new Promotion Product.
                            </DialogContentText>
                            <Box sx={{pb: 2}}>
                                <ForecastProductMultiSelectDropdown
                                    forecastId={forecastId}
                                    customerId={customerId}
                                    forecastPeriodId={forecastPeriodId}
                                    onProductsSelected={handleProductsSelected}
                                    productErrorText={skuBaseForecastErrorText}
                                    disableCloseOnSelect={true}
                                />
                            </Box>
                            <Box sx={{pb: 2}}>
                                <TextField
                                    label="RRP"
                                    name="recommendedRetailPrice"
                                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                                    value={promotionProductFormData.recommendedRetailPrice}
                                    error={Boolean(recommendedRetailPriceErrorText)}
                                    helperText={recommendedRetailPriceErrorText}
                                    onChange={handleTextFieldChange}
                                />
                            </Box>
                            <Box sx={{pb: 2}}>
                                <TextField
                                    label={selectedPromotion.promotionTypeId === 3 ? "Promotional Cost Price" : "Unit Funding"}
                                    name="unitFunding"
                                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                                    value={promotionProductFormData.unitFunding}
                                    error={Boolean(unitFundingErrorText)}
                                    helperText={unitFundingErrorText}
                                    onChange={handleTextFieldChange}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose} disabled={loadingPromotionProducts}>
                                Cancel
                            </Button>
                            <Button
                                onClick={() => handleCreatePromoProduct(promotionProductFormData)}
                                disabled={loadingPromotionProducts}
                            >
                                Create
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}

            {/* Distribution Edit Modal */}
            <Dialog 
                open={openDistributionEditDialog} 
                onClose={() => setOpenDistributionEditDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperComponent={PaperComponent}
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon color="primary" />
                        <Typography variant="h6">
                            Edit Promotion Volume
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {distributionEditData && (
                        <DistributionEditForm 
                            data={distributionEditData}
                            onSave={(updatedData) => {
                                const { row, field, cellKey } = distributionEditData;
                                const weekData = row?.weeklyDistributionRates?.find(w => w.week === field);
                                
                                // Store the values (NULL means use base rate / no uplift)
                                const newBaseValue = updatedData.baseValue; // Can be NULL
                                const newUpliftPercent = updatedData.percentUplift; // Can be NULL
                                
                                // Update the edited row with the new base value
                                const updatedRow = { ...row };
                                const weekIndex = updatedRow.weeklyDistributionRates.findIndex(w => w.week === field);
                                if (weekIndex !== -1) {
                                    updatedRow.weeklyDistributionRates[weekIndex].basePromoDistributionRate = newBaseValue;
                                    setEditedPromotionProductRows(prev => ({
                                        ...prev,
                                        [row.promotionProductId]: updatedRow
                                    }));
                                }
                                
                                // Update percent uplift
                                setPercentUpliftValues(prev => ({
                                    ...prev,
                                    [cellKey]: newUpliftPercent
                                }));
                                
                                // Mark as having unsaved changes
                                setUnsavedCellChanges(prev => new Set([...prev, cellKey]));
                                
                                setOpenDistributionEditDialog(false);
                            }}
                            onCancel={() => setOpenDistributionEditDialog(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* RRP Edit Modal */}
            <Dialog 
                open={openRRPEditDialog} 
                onClose={() => setOpenRRPEditDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperComponent={PaperComponent}
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon color="primary" />
                        <Typography variant="h6">
                            Edit RRP
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {rrpEditData && (
                        <RRPEditForm
                            data={rrpEditData}
                            onSave={(updatedData) => {
                                if (updatedData.selectedCells && updatedData.selectedCells.length > 1) {
                                    handleBulkRRPSave(updatedData);
                                    return;
                                }

                                const { row, cellKey } = rrpEditData;
                                const { value } = updatedData;
                                
                                // Get or create edited row
                                let editedRow = editedPromotionProductRows[row.promotionProductId];
                                if (!editedRow) {
                                    editedRow = JSON.parse(JSON.stringify(row));
                                }
                                
                                // Update the RRP value
                                editedRow.recommendedRetailPrice = value;
                                
                                // Update edited rows
                                setEditedPromotionProductRows(prev => ({
                                    ...prev,
                                    [row.promotionProductId]: editedRow
                                }));
                                
                                // Check if value changed from original to determine if we should track unsaved changes
                                const valueChanged = value !== row.recommendedRetailPrice;
                                
                                if (valueChanged) {
                                    setUnsavedCellChanges(prev => new Set([...prev, cellKey]));
                                } else {
                                    setUnsavedCellChanges(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(cellKey);
                                        return newSet;
                                    });
                                }
                                
                                setOpenRRPEditDialog(false);
                            }}
                            onCancel={() => setOpenRRPEditDialog(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Unit Funding Edit Modal */}
            <Dialog 
                open={openUnitFundingEditDialog} 
                onClose={() => setOpenUnitFundingEditDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperComponent={PaperComponent}
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon color="primary" />
                        <Typography variant="h6">
                            Edit {selectedPromotion?.promotionTypeId === 3 ? "Promotional Cost Price" : "Unit Funding"}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {unitFundingEditData && (
                        <UnitFundingEditForm
                            data={{
                                ...unitFundingEditData,
                                headerName: selectedPromotion?.promotionTypeId === 3 ? "Promotional Cost Price" : "Unit Funding"
                            }}
                            onSave={(updatedData) => {
                                if (updatedData.selectedCells && updatedData.selectedCells.length > 1) {
                                    handleBulkUnitFundingSave(updatedData);
                                    return;
                                }

                                const { row, cellKey } = unitFundingEditData;
                                const { value } = updatedData;
                                
                                // Get or create edited row
                                let editedRow = editedPromotionProductRows[row.promotionProductId];
                                if (!editedRow) {
                                    editedRow = JSON.parse(JSON.stringify(row));
                                }
                                
                                // Update the Unit Funding value
                                editedRow.unitFunding = value;
                                
                                // Update edited rows
                                setEditedPromotionProductRows(prev => ({
                                    ...prev,
                                    [row.promotionProductId]: editedRow
                                }));
                                
                                // Check if value changed from original to determine if we should track unsaved changes
                                const valueChanged = value !== row.unitFunding;
                                
                                if (valueChanged) {
                                    setUnsavedCellChanges(prev => new Set([...prev, cellKey]));
                                } else {
                                    setUnsavedCellChanges(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(cellKey);
                                        return newSet;
                                    });
                                }
                                
                                setOpenUnitFundingEditDialog(false);
                            }}
                            onCancel={() => setOpenUnitFundingEditDialog(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Bulk Distribution Edit Modal - now uses unified DistributionEditForm */}
            <Dialog 
                open={openBulkDistributionEditDialog} 
                onClose={() => setOpenBulkDistributionEditDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperComponent={PaperComponent}
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon color="primary" />
                        <Typography variant="h6">
                            Edit Promotion Volume
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {bulkDistributionEditData && (
                        <DistributionEditForm 
                            data={bulkDistributionEditData}
                            onSave={(updatedData) => {
                                handleBulkDistributionSave(updatedData);
                            }}
                            onCancel={() => setOpenBulkDistributionEditDialog(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {!!snackbar && (
                <Snackbar
                    open
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    onClose={handleCloseSnackbar}
                    autoHideDuration={6000}
                >
                    <Alert {...snackbar} onClose={handleCloseSnackbar}/>
                </Snackbar>
            )}
            <Dialog
                open={openConfirmationDialog}
                onClose={handleCloseConfirmationDialog}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{cursor: 'move'}} id="draggable-dialog-title">
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmingDeletionType === 'promotion'
                            ? 'Are you sure you want to delete this promotion? This action cannot be undone.'
                            : 'Are you sure you want to remove this SKU from its promotion? This action cannot be undone.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmationDialog} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (confirmingDeletionType === 'promotion') {
                                handleConfirmDeletePromotion();
                            } else if (confirmingDeletionType === 'promotionProduct') {
                                handleConfirmDeletePromotionProduct();
                            }
                        }}
                        color="primary"
                    >
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
            
            <ResetDialog
                open={resetDialog.open}
                currentValue={resetDialog.currentValue}
                baseValue={resetDialog.baseValue}
                onClose={handleResetDialogClose}
                onConfirm={handleResetOverrideConfirm}
                includesUplift={true}
            />
        </Box>
    );
}
